/**
 * Admin → Approvals: everything staff and website owners are about to make live across every Xelent Sites website
 * (pending approvals, scheduled publishes, scheduled social posts) and what went live in the last 7 days.
 * Every action asks for an explicit confirm and is audited in the cloud.
 */
import { useEffect, useRef, useState } from "preact/hooks";

type Who = { name: string; kind: string; email?: string };
type Item = {
  key: string; section: "pending" | "scheduled" | "published" | "social"; siteId: string; siteName: string; siteUrl: string | null;
  who: Who; reviewer?: Who | null; title: string; type: string; when: string | null; previewUrl: string | null;
  approvalRequestId?: string; changeSetId?: string; releaseId?: string; publishError?: string | null; exhausted?: boolean; networks?: string[];
};
type Inbox = { generatedAt: string; oldestRefresh: string | null; stale: number; items: Item[]; counts: Record<Item["section"], number>; sites: number; errors: Array<{ siteId: string; message: string }>; social: "on" | "not_available" };
type Action = "approve" | "reject" | "cancel";

async function call<T = any>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`/api${path}`, { headers: { "content-type": "application/json" }, ...init });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export const OVERSIGHT_CSS = `
.ov-badge{display:inline-block;min-width:18px;padding:0 5px;margin-left:5px;border-radius:9px;background:var(--accent);color:#fff;font-size:11px;font-weight:700;line-height:18px;text-align:center}
.ov-sec{margin:22px 0 8px;display:flex;align-items:baseline;gap:8px}.ov-sec h3{margin:0}.ov-sec span{color:var(--muted);font-size:12.5px}
.ov-list{display:flex;flex-direction:column;gap:8px}
.ov-row{background:var(--bg2);border:1px solid var(--line);border-radius:10px;padding:10px 12px;display:grid;grid-template-columns:1fr auto;gap:6px 14px;align-items:center}
.ov-row .t{font-weight:600;overflow-wrap:anywhere}.ov-row .m{color:var(--muted);font-size:12.5px;display:flex;flex-wrap:wrap;gap:4px 12px}
.ov-row .acts{display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.ov-pill{font-size:11px;padding:1px 7px;border-radius:9px;border:1px solid var(--line);color:var(--muted)}
.ov-pill.staff{color:#7fb3ff;border-color:#2c4a6e}.ov-pill.owner{color:var(--ok);border-color:#2d5a41}.ov-pill.warn{color:var(--warn);border-color:#6b5520}
.ov-modal{position:fixed;inset:0;background:rgba(0,0,0,.55);display:grid;place-items:center;z-index:50;padding:16px}
.ov-modal .card{width:min(520px,100%)}
.ov-tbl td,.ov-tbl th{white-space:nowrap}.ov-tbl td.wrap{white-space:normal}
.ov-scroll{overflow-x:auto}
.ov-person{cursor:pointer}.ov-person:hover td{background:var(--bg3)}
@media (max-width:700px){.ov-row{grid-template-columns:1fr}.ov-row .acts{justify-content:flex-start}}
`;

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const rel = (iso: string | null) => {
  if (!iso) return "";
  const d = (Date.parse(iso) - Date.now()) / 60000;
  const a = Math.abs(d);
  const s = a < 60 ? `${Math.round(a)} min` : a < 1440 ? `${Math.round(a / 60)} h` : `${Math.round(a / 1440)} d`;
  return d >= 0 ? `in ${s}` : `${s} ago`;
};

/** Pending approvals across every website, refreshed every 60 s (the cloud caches the fan-out for 60 s). */
export function usePendingCount(enabled: boolean): number | null {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const load = () => call("/admin/inbox/count").then((r) => setN(r.pending)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    const onChange = () => load();
    window.addEventListener("ov-inbox-changed", onChange);
    return () => {
      clearInterval(t);
      window.removeEventListener("ov-inbox-changed", onChange);
    };
  }, [enabled]);
  return n;
}

function WhoPill({ who }: { who: Who }) {
  const label = who.kind === "staff" ? "staff" : who.kind === "owner" ? "website owner" : who.kind === "platform" ? "owner" : "";
  return (
    <span title={who.email ?? ""}>
      {who.name} {label && <span class={`ov-pill ${who.kind}`}>{label}</span>}
    </span>
  );
}

function Confirm({ item, action, onClose, onDone }: { item: Item; action: Action; onClose: () => void; onDone: (msg: string) => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const verb = action === "approve" ? "Approve and publish now" : action === "reject" ? "Reject" : "Cancel the scheduled publish";
  const effect =
    action === "approve"
      ? `This publishes the change to ${item.siteName} right away${item.siteUrl ? ` (${item.siteUrl})` : ""}. Visitors see it within a minute.`
      : action === "reject"
        ? "The change is not published. The person who asked sees your reason."
        : `Nothing goes live on ${fmt(item.when)}. The change returns to draft so it can be edited and resubmitted.`;
  const go = async () => {
    if (action === "reject" && reason.trim().length < 2) return setErr("Write a short reason.");
    setBusy(true);
    setErr("");
    try {
      await call("/admin/inbox/action", { method: "POST", body: JSON.stringify({ action, siteId: item.siteId, approvalRequestId: item.approvalRequestId, reason: reason.trim() || undefined, confirm: `${action.toUpperCase()} ${item.approvalRequestId}` }) });
      window.dispatchEvent(new Event("ov-inbox-changed"));
      onDone(action === "approve" ? `Published on ${item.siteName}.` : action === "reject" ? "Rejected." : "Schedule cancelled.");
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div class="ov-modal" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div class="card" role="dialog" aria-modal="true" aria-label={verb}>
        <h1 style="font-size:18px">{verb}?</h1>
        <p style="margin:8px 0 4px;color:var(--fg)"><b>{item.title}</b></p>
        <p style="margin:0 0 10px">{item.siteName} · {item.type} · from {item.who.name}</p>
        <p style="margin:0 0 14px">{effect}</p>
        {(action === "reject" || action === "cancel") && (
          <div class="field">
            <label>{action === "reject" ? "Reason (required, sent to the person)" : "Reason (optional)"}</label>
            <textarea class="input" rows={3} value={reason} onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)} />
          </div>
        )}
        {err && <div class="error">{err}</div>}
        <div style="display:flex;gap:8px;justify-content:flex-end">
          <button class="btn" disabled={busy} onClick={onClose}>Keep as is</button>
          <button class={`btn ${action === "approve" ? "primary" : "danger"}`} disabled={busy} onClick={go}>{busy ? "Working…" : verb}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ it, onAct }: { it: Item; onAct: (a: Action) => void }) {
  return (
    <div class="ov-row">
      <div>
        <div class="t">{it.title}</div>
        <div class="m">
          <span>{it.siteUrl ? <a href={it.siteUrl} target="_blank" rel="noopener">{it.siteName}</a> : it.siteName}</span>
          <WhoPill who={it.who} />
          <span>{it.type}{it.networks?.length ? ` · ${it.networks.join(", ")}` : ""}</span>
          <span title={fmt(it.when)}>{it.section === "pending" ? `asked ${rel(it.when)}` : it.section === "published" ? `live ${rel(it.when)}` : `${fmt(it.when)} (${rel(it.when)})`}</span>
          {it.reviewer && it.section !== "pending" && <span>approved by {it.reviewer.name}</span>}
          {it.publishError && <span class={`ov-pill warn`} title={it.publishError}>{it.exhausted ? "gave up after 5 tries" : "last try failed"}</span>}
        </div>
      </div>
      <div class="acts">
        {it.previewUrl && <a class="btn small" href={it.previewUrl} target="_blank" rel="noopener">{it.section === "published" ? "Open site" : "Preview"}</a>}
        {it.section === "pending" && <button class="btn small primary" onClick={() => onAct("approve")}>Approve now</button>}
        {it.section === "pending" && <button class="btn small danger" onClick={() => onAct("reject")}>Reject</button>}
        {it.section === "scheduled" && <button class="btn small danger" onClick={() => onAct("cancel")}>Cancel schedule</button>}
      </div>
    </div>
  );
}

export function InboxView() {
  const [d, setD] = useState<Inbox | null>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [site, setSite] = useState("");
  const siteRef = useRef("");
  siteRef.current = site;
  const [act, setAct] = useState<{ item: Item; action: Action } | null>(null);
  const [toast, setToast] = useState("");
  // Show the snapshot at once, then bring stale websites up to date (each platform read takes a second or two).
  const load = async (refresh: false | "1" | "force" = false) => {
    try {
      if (!refresh) {
        const r = await call<Inbox>("/admin/inbox");
        setD(r);
        setErr("");
        if (!r.stale) return;
      }
      setBusy(true);
      setD(await call<Inbox>(`/admin/inbox?refresh=${refresh || "1"}${refresh === "force" && siteRef.current ? `&site=${siteRef.current}` : ""}`));
      setErr("");
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => load("1"), 60000);
    return () => clearInterval(t);
  }, []);
  const items = (d?.items ?? []).filter((i) => !site || i.siteId === site);
  const sites = [...new Map((d?.items ?? []).map((i) => [i.siteId, i.siteName])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const sec = (k: Item["section"]) => items.filter((i) => i.section === k);
  const block = (k: Item["section"], title: string, sub: string, empty: string) => (
    <>
      <div class="ov-sec"><h3>{title} ({sec(k).length})</h3><span>{sub}</span></div>
      <div class="ov-list">
        {sec(k).length === 0 ? <div class="hint">{empty}</div> : sec(k).map((it) => <Row key={it.key} it={it} onAct={(a) => setAct({ item: it, action: a })} />)}
      </div>
    </>
  );
  return (
    <div class="admin">
      <style>{OVERSIGHT_CSS}</style>
      <h2>Approvals</h2>
      <p class="hint">Everything staff and website owners are about to make live on Xelent Sites, across every website. Staff with the publish right and website owners can publish without you; this is where you see it and stop it.</p>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap">
        <select class="input" style="width:auto" value={site} onChange={(e) => setSite((e.target as HTMLSelectElement).value)}>
          <option value="">All websites{d ? ` (${d.sites})` : ""}</option>
          {sites.map(([id, name]) => <option value={id}>{name}</option>)}
        </select>
        <button class="btn small" disabled={busy} onClick={() => load("force")}>{busy ? "Checking…" : "Refresh"}</button>
        {d && <span class="hint">{busy ? `Checking ${d.stale || ""} website${d.stale === 1 ? "" : "s"} for changes…` : `Up to date as of ${rel(d.oldestRefresh) || "now"}`} · {d.sites} websites</span>}
      </div>
      {toast && <div class="hint" style="color:var(--ok);margin-top:8px">{toast}</div>}
      {err && <div class="error">{err}</div>}
      {!d && !err && <div class="hint" style="margin-top:14px">Loading…</div>}
      {d && d.oldestRefresh === null && busy && <div class="hint" style="margin-top:14px">Reading every website for the first time (about half a minute)…</div>}
      {d && (
        <>
          {block("pending", "Waiting for approval", "submitted, not live", "Nothing waiting.")}
          {block("scheduled", "Scheduled", "approved, goes live on its date", "Nothing scheduled.")}
          {d.social === "on" ? block("social", "Scheduled social posts", "the next 60 days", "No social posts scheduled.") : <><div class="ov-sec"><h3>Scheduled social posts</h3></div><div class="hint">Shows here once the platform's social tools are live.</div></>}
          {block("published", "Went live in the last 7 days", "newest first", "Nothing published this week.")}
          {d.errors.length > 0 && <p class="hint" style="margin-top:16px">Could not read {d.errors.length} website(s): {d.errors.map((e) => e.siteId).join(", ")}</p>}
        </>
      )}
      {act && <Confirm item={act.item} action={act.action} onClose={() => setAct(null)} onDone={(m) => { setAct(null); setToast(m); load(); }} />}
    </div>
  );
}
